import { Checkbox } from 'components/FormUtils/Checkbox'
import { TextInput } from 'components/FormUtils/TextInput'
import { Fragment, useState } from 'react'
import { AiOutlineWarning } from 'react-icons/ai'
import { ChevronDown, ChevronUp } from 'lucide-react'

import { formatInteger, formatUsdDecimal } from '../../../utils/formatting'
import { NumericInput } from '../../FormUtils/NumericInput'
import Select from '../../FormUtils/Select'
import { cn } from '../../UI/cn'
import { DealsFormValues } from './CreateDealModal'
import { Control, useFormState, useWatch } from 'react-hook-form'
import apiService from 'services/api'
import { featureFlagService } from 'utils/featureFlagService'
import { CompanyTypeSlug } from 'models/companies'

export default function DealDetailsStep({
  predictions,
  control,
  companyTypeSlug,
  existingDealsByProduct,
  originCampaignId,
  showDealName = true,
  companyDetails,
}: {
  predictions?: any[]
  control: Control<DealsFormValues>
  companyTypeSlug: CompanyTypeSlug
  existingDealsByProduct?: Record<number, number>
  originCampaignId?: number
  showDealName?: boolean
  companyDetails?: any
}) {
  const api = apiService()
  const featureFlag = featureFlagService()
  const { data: stageOptions } = api.useGetSalesStageOptions()
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })

  // Filter campaigns by the contact company's sub_category slug if available
  // This ensures only campaigns matching the opportunity type are shown
  const campaignTypeFilter =
    companyDetails?.sub_category?.slug ?? companyTypeSlug
  const { data: campaignOptions } =
    api.useGetCampaignsOptions(campaignTypeFilter)

  const { data: products } = api.useGetProducts()
  const [overridesExpanded, setOverridesExpanded] = useState(false)

  const watch = useWatch({ control })
  const { errors } = useFormState({ control })

  return (
    <Fragment>
      <div className="gap-4">
        {predictions?.map((item, index) => {
          const active = watch?.deals?.[index]?.create
          const stage = watch?.deals?.[index]?.sales_stage
          const avgDaysToClose = stageOptions?.find((it) => it.value === stage)
            ?.original?.avg_days_to_close

          const date = new Date()
          if (avgDaysToClose) {
            date.setDate(date.getDate() + avgDaysToClose)
          }

          const existingDeals =
            existingDealsByProduct?.[item.product?.id ?? -1] ?? 0

          return (
            <div
              key={index}
              className="w-full bg-white rounded-lg shadow-md p-3 border border-gray-200 mb-3"
            >
              <div
                className={cn('flex flex-row gap-2 items-center', {
                  'mb-4': active,
                })}
              >
                <Checkbox
                  control={control}
                  name={`deals.${index}.create`}
                  wrapperStyle={{ width: 'unset' }}
                />
                <h3 className="text-lg text-gray-800 flex-1">
                  Product: <b>{item.product.name}</b>
                </h3>
              </div>

              <div
                className={cn({
                  hidden: !active,
                })}
              >
                {/* Deal Name Field */}
                {active && showDealName && (
                  <div className="mb-4">
                    <TextInput
                      control={control}
                      name={`deals.${index}.name`}
                      label="Deal Name"
                      placeholder={
                        companyDetails?.name ||
                        'Enter custom deal name (optional)'
                      }
                      optional={true}
                      disabled={!active}
                      maxLength={100}
                    />
                  </div>
                )}

                {/* Product Variant Selection */}
                {active && products && (
                  <div className="mb-2">
                    <Select
                      options={(() => {
                        const selectedProduct = products.find(
                          (p) => p.id === item.product.id
                        )
                        if (!selectedProduct?.variants?.length) {
                          return [
                            { label: 'No variants available', value: null },
                          ]
                        }
                        return [
                          {
                            label: 'Select Product Variant',
                            value: null,
                          },
                          ...selectedProduct.variants.map((variant) => ({
                            label: variant.name,
                            value: variant.id,
                          })),
                        ]
                      })()}
                      control={control}
                      name={`deals.${index}.product_variant`}
                      label="Select Product Variant"
                      optional={true}
                      disabled={!active}
                    />
                  </div>
                )}

                <div
                  className={cn(
                    'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 '
                  )}
                >
                  <div>
                    <Select
                      options={stageOptions}
                      control={control}
                      name={`deals.${index}.sales_stage`}
                      label={'Sales Stage'}
                      disabled={!active}
                    />
                    <span className="text-sm text-gray-600 mt-2">
                      {stage ? (
                        <Fragment>
                          <b>Estimated Close Date:</b>{' '}
                          {date.toLocaleDateString('en-US')}
                        </Fragment>
                      ) : (
                        ' '
                      )}
                    </span>
                  </div>
                  <div>
                    <Select
                      options={userOptions}
                      control={control}
                      name={`deals.${index}.account_owner`}
                      label="Account Owner"
                      disabled={!active}
                    />
                    {item.predicted_revenue ? (
                      <span className="text-sm text-gray-600 mt-2">
                        <b>Predicted Monthly Revenue:</b>{' '}
                        {formatUsdDecimal(item.predicted_revenue)}
                      </span>
                    ) : null}
                  </div>
                  <div>
                    {featureFlag.enableCampaigns ? (
                      <Fragment>
                        <Select
                          options={campaignOptions}
                          control={control}
                          name={`deals.${index}.origin_campaign_id`}
                          label="Origin Campaign"
                          disabled={!active || originCampaignId != undefined}
                        />
                        {item.predicted_volume ? (
                          <span className="text-sm text-gray-600 mt-2">
                            <b>Predicted Monthly Volume:</b>{' '}
                            {formatInteger(item.predicted_volume ?? 0)}
                          </span>
                        ) : null}
                      </Fragment>
                    ) : null}
                  </div>
                </div>

                {/* Deal Overrides Section */}
                {active && (
                  <div className="mt-6">
                    <div className="bg-[#f4f7f5] rounded-lg p-4">
                      <button
                        type="button"
                        onClick={() => setOverridesExpanded(!overridesExpanded)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900">
                            Deal Overrides
                          </h4>
                          <span className="bg-[#d5dcd9] text-[#6b7280] text-xs px-2 py-1 rounded-full">
                            Optional
                          </span>
                        </div>
                        {overridesExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                      </button>

                      {overridesExpanded && (
                        <div className="mt-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <NumericInput
                              control={control}
                              fieldName={`deals.${index}.monthly_volume_override`}
                              label="Est. Monthly Volume"
                              optional={true}
                              disabled={!active}
                            />
                            <NumericInput
                              control={control}
                              fieldName={`deals.${index}.monthly_revenue_override`}
                              label="Est. Monthly Revenue"
                              optional={true}
                              disabled={!active}
                            />
                            <TextInput
                              label="Est. Close Date"
                              control={control}
                              name={`deals.${index}.close_date_override`}
                              type="date"
                              optional={true}
                              disabled={!active}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/*// @ts-expect-error ignore */}
                {errors?.['custom']?.message && (
                  <div className="text-red-500 mt-4">
                    {/*// @ts-expect-error ignore */}
                    {errors.custom.message}
                  </div>
                )}
                {existingDeals ? (
                  <div className="pt-3 flex items-center gap-2">
                    <AiOutlineWarning size={14} color="#FCBD00" />
                    {existingDeals > 1 ? (
                      <span>
                        Some companies in your selection already have a deal for
                        this product.
                      </span>
                    ) : (
                      <span>
                        The company you selected already has a deal for this
                        product.
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </Fragment>
  )
}
